import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE, GREEN] = CHART_COLORS;
const TANK = { left: 130, right: 430, top: 40, bottom: 230 };
const WATER_TOP = 90;
const BLOCK = { width: 90, height: 90 };

/**
 * A block in a tank. A floating block sits with `fraction` of its height below the
 * waterline; a sinking one rests fully submerged on the bottom. The arrows are scaled to
 * the two forces, W down and F_b up, within sensible limits.
 */
export function BuoyancyDiagram({
  id,
  label,
  description,
  fraction,
  sinks,
  forceRatio,
}: {
  id: string;
  label: string;
  description: string;
  /** Fraction of the block below the surface, 0–1. */
  fraction: number;
  sinks: boolean;
  /** F_b / W, for the arrow lengths. */
  forceRatio: number;
}) {
  const cx = (TANK.left + TANK.right) / 2;
  const top = sinks
    ? TANK.bottom - BLOCK.height
    : WATER_TOP - (1 - fraction) * BLOCK.height;
  const down = `${id}-down`;
  const up = `${id}-up`;
  const weightLength = 56;
  const buoyLength = Math.min(Math.max(weightLength * forceRatio, 14), 104);
  const mid = top + BLOCK.height / 2;
  return (
    <ChartSvg
      description={description}
      height={254}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <marker
          id={down}
          markerHeight="8"
          markerWidth="8"
          orient="auto"
          refX="7"
          refY="4"
        >
          <path d="M0,0 L8,4 L0,8 z" fill={ORANGE} />
        </marker>
        <marker
          id={up}
          markerHeight="8"
          markerWidth="8"
          orient="auto"
          refX="7"
          refY="4"
        >
          <path d="M0,0 L8,4 L0,8 z" fill={GREEN} />
        </marker>
      </defs>
      <rect
        fill={BLUE}
        fillOpacity="0.22"
        height={TANK.bottom - WATER_TOP}
        width={TANK.right - TANK.left}
        x={TANK.left}
        y={WATER_TOP}
      />
      <path
        d={`M ${TANK.left} ${TANK.top} L ${TANK.left} ${TANK.bottom} L ${TANK.right} ${TANK.bottom} L ${TANK.right} ${TANK.top}`}
        fill="none"
        stroke={CHART_INK.text}
        strokeWidth="3"
      />
      <rect
        fill={CHART_INK.muted}
        fillOpacity="0.85"
        height={BLOCK.height}
        width={BLOCK.width}
        x={cx - BLOCK.width / 2}
        y={top}
      />
      <line
        markerEnd={`url(#${down})`}
        stroke={ORANGE}
        strokeWidth="3"
        x1={cx - 20}
        x2={cx - 20}
        y1={mid}
        y2={mid + weightLength}
      />
      <line
        markerEnd={`url(#${up})`}
        stroke={GREEN}
        strokeWidth="3"
        x1={cx + 20}
        x2={cx + 20}
        y1={mid}
        y2={mid - buoyLength}
      />
      <text
        fill={ORANGE}
        fontSize="15"
        fontStyle="italic"
        textAnchor="end"
        x={cx - 28}
        y={mid + weightLength - 2}
      >
        W
      </text>
      <text
        fill={GREEN}
        fontSize="15"
        fontStyle="italic"
        textAnchor="start"
        x={cx + 28}
        y={mid - buoyLength + 8}
      >
        F_b
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="start"
        x={TANK.right + 10}
        y={WATER_TOP + 5}
      >
        ρ
      </text>
    </ChartSvg>
  );
}
